import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Loader2, CreditCard, PhoneForwarded, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const eur = (c) => `${((c || 0) / 100).toFixed(2).replace('.', ',')} €`;
const STATUS = {
  paid: { label: 'BEZAHLT', cls: 'bg-green-50 text-green-700', icon: CheckCircle2 },
  pending: { label: 'OFFEN', cls: 'bg-[#fff8e1] text-[#8a6d1a]', icon: Clock },
  failed: { label: 'FEHLGESCHLAGEN', cls: 'bg-red-50 text-red-700', icon: XCircle },
  expired: { label: 'ABGELAUFEN', cls: 'bg-gray-100 text-gray-600', icon: Clock },
  refunded: { label: 'ERSTATTET', cls: 'bg-blue-50 text-blue-700', icon: CheckCircle2 },
};

const Row = ({ t, onAbhaken }) => {
  const st = STATUS[t.payment_status] || STATUS.pending;
  const k = t.kontakt;
  const calls = k?.calls || [];
  return (
    <tr className={`border-t border-gray-100 align-top ${t.abgehakt ? 'opacity-60' : ''}`} data-testid={`zahlung-row-${t.session_id}`}>
      <td className="py-3 pr-3"><input type="checkbox" checked={!!t.abgehakt} onChange={(e) => onAbhaken(t, e.target.checked)} className="w-4 h-4 accent-[#b8893d] cursor-pointer" title={t.abgehakt ? `Abgehakt von ${t.abgehakt_von || '—'} am ${fmt(t.abgehakt_am)}` : 'Als bearbeitet/verbucht abhaken'} data-testid={`zahlung-abhaken-${t.session_id}`} /></td>
      <td className="py-3 pr-4 text-xs text-[#7a8a99] whitespace-nowrap">{fmt(t.created_at)}</td>
      <td className="py-3 pr-4 text-sm text-[#1B3A5C]">
        <div className="font-medium">{k ? `${k.firstName} ${k.lastName}` : '—'}</div>
        <div className="text-xs text-[#7a8a99]">{t.notfall_id} · {k?.phone}</div>
      </td>
      <td className="py-3 pr-4 text-sm font-semibold text-[#1B3A5C] whitespace-nowrap">{eur(t.amount)}</td>
      <td className="py-3 pr-4 text-xs text-[#34536b] capitalize">{t.provider}</td>
      <td className="py-3 pr-4"><span className={`inline-flex items-center gap-1 text-[10px] tracking-wider px-2 py-0.5 rounded-sm font-semibold ${st.cls}`}><st.icon className="w-3 h-3" />{st.label}</span></td>
      <td className="py-3 text-xs text-[#34536b]">
        {calls.length === 0 ? <span className="text-[#9aa5b1]">keine Verbindung</span> : calls.map((c, i) => (
          <div key={c.sid || i} className="flex items-center gap-1"><PhoneForwarded className="w-3 h-3 text-[#b8893d]" />{c.status}{c.duration_sec != null ? ` · ${Math.round(c.duration_sec / 60)} Min.` : ''} <span className="text-[#9aa5b1]">({fmt(c.started_at)})</span></div>
        ))}
      </td>
    </tr>
  );
};

const AdminZahlungen = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);

  const abhaken = async (t, abgehakt) => {
    try { await api.patch(`/admin/zahlungen/${t.session_id}/abhaken`, { abgehakt }); setData((d) => ({ ...d, items: d.items.map((x) => (x.session_id === t.session_id ? { ...x, abgehakt } : x)) })); }
    catch (e) { toast({ title: 'Fehler', description: 'Konnte nicht gespeichert werden', variant: 'destructive' }); }
  };
  const load = useCallback(async () => {
    try { setData((await api.get('/admin/zahlungen')).data); }
    catch (e) { toast({ title: 'Fehler', description: 'Zahlungen konnten nicht geladen werden.', variant: 'destructive' }); console.warn(e); }
  }, [toast]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['admin', 'kanzlei_mitarbeiter'].includes(user.role)) { navigate('/login?next=/admin/zahlungen'); return; }
    load();
  }, [authLoading, user, navigate, load]);

  return (
    <div className="min-h-screen bg-[#f5f8fa]">
      <Header />
      <main className="pt-[150px] pb-20">
        <section className="bg-[#1B3A5C] text-white py-10">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-2"><span className="w-12 h-px bg-[#b8893d]"></span>NOTFALLHOTLINE</div>
            <h1 className="font-serif font-light text-3xl md:text-4xl tracking-tight flex items-center gap-3"><CreditCard className="w-8 h-8 text-[#b8893d]" /> Zahlungen</h1>
            {data && (
              <div className="flex flex-wrap gap-6 mt-4 text-sm" data-testid="zahlungen-summary">
                <div><div className="text-white/60 text-xs tracking-wider">BEZAHLT</div><div className="font-serif text-2xl">{data.paid} / {data.total}</div></div>
                <div><div className="text-white/60 text-xs tracking-wider">SUMME</div><div className="font-serif text-2xl">{eur(data.sum_paid_cents)}</div></div>
                <div><div className="text-white/60 text-xs tracking-wider">ANBIETER</div><div className="text-xs mt-2">Stripe ✓ · PayPal {data.config.paypal ? '✓' : '– (Keys fehlen)'} · Twilio {data.config.twilio ? '✓' : '– (Fallback: Nummer wird angezeigt)'}</div></div>
              </div>
            )}
          </div>
        </section>
        <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-8">
          {!data ? <div className="flex items-center gap-2 text-[#34536b] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lade …</div>
            : data.items.length === 0 ? <p className="text-sm text-[#7a8a99] bg-white p-6 rounded-sm border border-gray-100" data-testid="zahlungen-empty">Noch keine Zahlungen.</p>
            : (
              <div className="bg-white border border-gray-100 rounded-sm shadow-sm p-5 overflow-x-auto">
                <table className="w-full text-left" data-testid="zahlungen-table">
                  <thead><tr className="text-[10px] tracking-wider text-[#7a8a99]"><th className="pb-2 pr-3" title="Abgehakt">✓</th><th className="pb-2 pr-4">DATUM</th><th className="pb-2 pr-4">MANDANT</th><th className="pb-2 pr-4">BETRAG</th><th className="pb-2 pr-4">ART</th><th className="pb-2 pr-4">STATUS</th><th className="pb-2">VERBINDUNG</th></tr></thead>
                  <tbody>{data.items.map((t) => <Row key={t.session_id} t={t} onAbhaken={abhaken} />)}</tbody>
                </table>
              </div>
            )}
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default AdminZahlungen;
